// Iter 82.10 / Phase E3.1 Sprint 3 — Bank Export panel inside Run Console.
//
// 3-step flow: choose format → pre-flight preview → export.
// Re-exports are allowed; each one appends a new audit row.
// Common controls C1-C7 are enforced by the backend; UI surfaces
// blockers + warnings + reconciliation totals.
import { useCallback, useEffect, useState } from "react";
import {
    Bank, Download, Warning, CheckCircle, ArrowsClockwise, FloppyDisk, Lock,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PayrollBankExportPanel({ runId, run, canEdit, onRefresh }) {
    const [formats, setFormats] = useState([]);
    const [exports, setExports] = useState([]);
    const [active, setActive] = useState("indusind_neft_v1");
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    const isLocked = run?.status === "Locked" || run?.status === "Disbursed";

    const loadFormats = useCallback(async () => {
        try {
            const r = await api.get("/payroll/payslip/meta");
            setFormats(r.data.bank_formats || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load formats"));
        }
    }, []);

    const loadHistory = useCallback(async () => {
        try {
            const r = await api.get(`/payroll/run/${runId}/bank-exports/list`);
            setExports(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load export history"));
        }
    }, [runId]);

    const loadPreview = useCallback(async () => {
        setPreviewLoading(true);
        try {
            const r = await api.get(`/payroll/run/${runId}/bank-export/preview`, {
                params: { format_code: active },
            });
            setPreview(r.data);
        } catch (err) {
            setPreview(null);
            toast.error(formatApiError(err, "Preview failed"));
        } finally {
            setPreviewLoading(false);
        }
    }, [runId, active]);

    useEffect(() => { loadFormats(); loadHistory(); }, [loadFormats, loadHistory]);
    useEffect(() => { if (isLocked) loadPreview(); }, [active, isLocked, loadPreview]);

    const triggerExport = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/payroll/run/${runId}/bank-export`, {
                format_code: active,
                accept_warnings: (preview?.warnings || []).length > 0,
            });
            toast.success(`Exported ${r.data.row_count} rows · ${inr(r.data.total_amount)}`);
            await loadHistory();
            onRefresh && onRefresh();
            // Immediately download the freshly produced file
            await downloadOne(r.data.id, r.data.filename);
        } catch (err) {
            toast.error(formatApiError(err, "Export failed"));
        } finally {
            setBusy(false);
        }
    };

    const downloadOne = async (exportId, filename) => {
        try {
            const r = await api.get(`/payroll/run/${runId}/bank-exports/${exportId}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
            await loadHistory();
        } catch (err) {
            toast.error(formatApiError(err, "Download failed"));
        }
    };

    const blockers = preview?.blockers || [];
    const warnings = preview?.warnings || [];
    const canExport = isLocked && canEdit && blockers.length === 0;

    return (
        <div className="space-y-4 pt-3" data-testid="bank-export-panel">
            {!isLocked && (
                <div className="border bg-amber-50 border-amber-200 text-amber-900 text-sm px-3 py-2 rounded flex items-start gap-2" data-testid="bank-export-locked-notice">
                    <Lock size={16} className="flex-shrink-0 mt-0.5" />
                    <span>Run is in <strong>{run?.status}</strong>. Bank export requires the run to be <strong>Locked</strong> first (Sprint 3 Control C1).</span>
                </div>
            )}

            <div className="flex flex-wrap items-end gap-2">
                <div className="flex flex-col">
                    <label className="text-xs text-slate-500 mb-1">Bank Export Format</label>
                    <select
                        value={active}
                        onChange={(e) => setActive(e.target.value)}
                        className="h-9 px-3 border rounded-md text-sm"
                        data-testid="bank-export-format-select"
                    >
                        {formats.map((f) => (
                            <option key={f.code} value={f.code}>{f.label}</option>
                        ))}
                    </select>
                </div>
                {isLocked && (
                    <Button variant="outline" size="sm" onClick={loadPreview} disabled={previewLoading} data-testid="bank-export-preview-btn">
                        <ArrowsClockwise size={14} className="mr-1" /> Refresh Preview
                    </Button>
                )}
                <span className="flex-1" />
                {canEdit && (
                    <Button size="sm" onClick={triggerExport} disabled={!canExport || busy} data-testid="bank-export-run-btn">
                        <Bank size={14} className="mr-1" /> Generate File
                    </Button>
                )}
            </div>

            {preview && (
                <div className="border rounded-lg bg-white p-3 space-y-2" data-testid="bank-export-preview">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-sm">
                            <strong>{preview.format_label}</strong>
                            <span className="text-xs text-slate-500 ml-2">({preview.format})</span>
                        </div>
                        <div className="text-xs text-slate-500">Filename: <span className="font-mono">{preview.filename}</span></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div data-testid="preview-expected-rows">
                            <div className="text-[10px] uppercase text-slate-400">Expected Rows</div>
                            <div className="font-mono font-semibold">{preview.expected_row_count}</div>
                        </div>
                        <div data-testid="preview-expected-total">
                            <div className="text-[10px] uppercase text-slate-400">Expected Total</div>
                            <div className="font-mono font-semibold">{inr(preview.expected_total_amount)}</div>
                        </div>
                        <div data-testid="preview-blockers-count">
                            <div className="text-[10px] uppercase text-slate-400">Blockers</div>
                            <div className={`font-mono font-semibold ${blockers.length ? "text-rose-700" : "text-emerald-700"}`}>
                                {blockers.length}
                            </div>
                        </div>
                        <div data-testid="preview-warnings-count">
                            <div className="text-[10px] uppercase text-slate-400">Warnings</div>
                            <div className={`font-mono font-semibold ${warnings.length ? "text-amber-700" : "text-slate-500"}`}>
                                {warnings.length}
                            </div>
                        </div>
                    </div>
                    {blockers.length > 0 && (
                        <div className="bg-rose-50 border-rose-200 border rounded p-2 mt-2" data-testid="preview-blockers">
                            <div className="flex items-center gap-1 text-rose-800 font-semibold text-sm">
                                <Warning size={14} /> Blockers — export is disabled
                            </div>
                            <ul className="text-xs text-rose-700 mt-1 list-disc list-inside space-y-0.5">
                                {blockers.map((b, i) => (
                                    <li key={i}><span className="font-mono">{b.code}</span>: {b.message}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {warnings.length > 0 && (
                        <div className="bg-amber-50 border-amber-200 border rounded p-2 mt-2" data-testid="preview-warnings">
                            <div className="flex items-center gap-1 text-amber-800 font-semibold text-sm">
                                <Warning size={14} /> Warnings — review before exporting
                            </div>
                            <ul className="text-xs text-amber-800 mt-1 list-disc list-inside space-y-0.5">
                                {warnings.map((w, i) => (
                                    <li key={i}><span className="font-mono">{w.code}</span>: {w.message}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            <section className="border rounded-lg bg-white overflow-hidden">
                <header className="px-4 py-2 border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    Export History (Control C4 — re-exports append, never overwrite)
                </header>
                <table className="w-full text-sm" data-testid="bank-export-history">
                    <thead className="bg-slate-50 text-xs text-slate-500">
                        <tr>
                            <th className="px-3 py-2 text-left">Format</th>
                            <th className="px-3 py-2 text-right">Rows</th>
                            <th className="px-3 py-2 text-right">Total</th>
                            <th className="px-3 py-2 text-left">SHA-256</th>
                            <th className="px-3 py-2 text-left">By</th>
                            <th className="px-3 py-2 text-left">At</th>
                            <th className="px-3 py-2 text-right">Downloads</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {exports.length === 0 && (
                            <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400" data-testid="bank-export-history-empty">No exports yet on this run.</td></tr>
                        )}
                        {exports.map((e) => (
                            <tr key={e.id} className="hover:bg-slate-50" data-testid={`bank-export-row-${e.id}`}>
                                <td className="px-3 py-2 text-xs">{e.format_label || e.format}</td>
                                <td className="px-3 py-2 text-right font-mono text-xs">{e.row_count}</td>
                                <td className="px-3 py-2 text-right font-mono text-xs">{inr(e.total_amount)}</td>
                                <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{(e.sha256 || "").slice(0, 16)}…</td>
                                <td className="px-3 py-2 text-xs text-slate-500">{e.actor_email}</td>
                                <td className="px-3 py-2 text-xs text-slate-500">{e.created_at?.slice(0, 19).replace("T", " ")}</td>
                                <td className="px-3 py-2 text-right text-xs font-mono">{e.downloaded_count || 0}</td>
                                <td className="px-3 py-2 text-right">
                                    <Button variant="ghost" size="sm" onClick={() => downloadOne(e.id, e.filename)} data-testid={`bank-export-download-${e.id}`}>
                                        <Download size={12} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        </div>
    );
}
